import { MenuService } from '@/layout/service/menu.service';
import { Component, computed, DestroyRef, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap, tap } from 'rxjs';

import { Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { DashboardPerfilService } from '@/features/portal/dashboard-profile/dashboard-profile.service';
import { PortalServicoService } from '@/core/api/sicom-api/portal/servico/portal-servico.service';
import { PortalServico } from '@/core/api/sicom-api/portal/servico/portal-servico.model';
import { PortalModuloService } from '@/core/api/sicom-api/portal/modulo/portal-modulo.service';
import { PortalModulo } from '@/core/api/sicom-api/portal/modulo/portal-modulo.model';
import { AnimateOnScroll } from 'primeng/animateonscroll';
import { item } from '@primeuix/themes/aura/dock';
import { EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { ConsultaReceita } from '@/shared/components/consulta/consulta-receita/consulta-receita';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { AcessoEmBreve } from '@/shared/components/acesso-em-breve/acesso-em-breve';
import { AcessoNegado } from '@/shared/components/acesso-negado/acesso-negado';
import { PermissionService } from '@/core/permissions/permission.service';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionInput } from '@/core/permissions/permission.model';

export interface CustomMenuItem extends MenuItem {
    description?: string;
    ribbon?: {
        show: boolean;
        color: string;
        text: string;
    };
    /** Role(s) exigida(s) pelo card do dashboard E pelo item do menu lateral. Sem ela → cinza/não-clicável. Array = canAny. */
    permission?: PermissionInput | PermissionInput[];
    /** Estado do card, calculado por `estadoDoCard` ao montar a lista. */
    estado?: CardEstado;
    items?: CustomMenuItem[]; // Ensure nested items also support description
}

export type CardEstado = 'normal' | 'em-breve' | 'sem-acesso';

/**
 * Estado do card. "Em breve" vence "Sem acesso": um módulo sem tela não deve dizer que falta permissão.
 * Card sem `permission` declarada permanece aberto — quatro cards não declaram nada (Gestão
 * Financeira, Gestão Administrativa, Gestão de Comissões, Especialidades). Especialidades é o
 * único desses quatro que também tem `ribbon.show: true`,
 * então na prática vira 'em-breve' antes de chegar na checagem de permission abaixo.
 */
export function estadoDoCard(item: CustomMenuItem, perm: PermissionService): CardEstado {
    if (item.ribbon?.show) return 'em-breve';
    if (!item.permission) return 'normal';
    const reqs = Array.isArray(item.permission) ? item.permission : [item.permission];
    return perm.canAny(reqs) ? 'normal' : 'sem-acesso';
}

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [RouterModule, AnimateOnScroll, ConsultaReceita, CardContainer, AcessoEmBreve, AcessoNegado],
    templateUrl: './dashboard.html',
    styleUrl: './dashboard.scss'
})
export class Dashboard implements OnInit, OnDestroy {
    private menuService = inject(MenuService);
    private router = inject(Router);
    private permissionService = inject(PermissionService);
    private readonly perfilService = inject(DashboardPerfilService);
    private readonly servicoService = inject(PortalServicoService);
    private readonly moduloService = inject(PortalModuloService);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Módulo (por URL) → permissão exigida no card. A tabela WEB_MODULO não guarda permissão, então
     * o gate de role continua no front. URL fora do mapa = card sem checagem de role (só in_ativo).
     */
    private readonly permissaoPorUrl: Record<string, PermissionInput | PermissionInput[]> = {
        '/arrecadacao': PERMISSOES.arrecadacao.modulo,
        '/divida-ativa': PERMISSOES.dividaAtiva.modulo,
        '/medicos': PERMISSOES.medicos.modulo,
        '/prestadores': PERMISSOES.prestadores.modulo,
        '/orgao': PERMISSOES.orgaos.modulo,
        '/servicos/pessoa-fisica': PERMISSOES.pessoaFisica.servicos.visualizar,
        '/servicos/pessoa-juridica': PERMISSOES.pessoaJuridica.servicos.visualizar,
        '/comissoes/dashboard': PERMISSOES.comissoes.modulo
    };

    /** UF do usuário logado; regional = CRM-{uf}, ou CFM quando a UF é BR. */
    protected readonly uf = signal('');
    protected readonly regional = computed(() => {
        const uf = this.uf();
        return uf === 'BR' ? 'CFM' : uf ? `CRM-${uf}` : 'CRM';
    });
    /** Serviços do portal (WEB_SERVICO) da UF do usuário, não divulgados — bloco do regional. */
    protected readonly servicosPortal = signal<PortalServico[]>([]);
    /** Serviços divulgados do CFM (uf=BR, divulga=true) — bloco "Plataformas e Soluções Digitais do CFM". */
    protected readonly servicosCfm = signal<PortalServico[]>([]);

    dashboardMenu: CustomMenuItem[] = [
        {
            label: 'Home',
            items: [{ label: 'Dashboard', icon: 'pi pi-fw pi-home', routerLink: ['/dashboard'] }]
        },
        {
            label: 'Gestão',
            items: [
                {
                    label: 'Gestão Financeira',
                    icon: 'pi pi-fw pi-dollar',
                    description: 'Acesse recursos do módulo de Gestão Financeira através de uma interface de navegação intuitiva. Gerencie o fluxo de caixa, contas a pagar e receber, e acompanhe o desempenho financeiro da sua organização.',
                    command: () => this.executar('Gestão Financeira', '/gestao-financeira')
                },
                {
                    label: 'Gestão Administrativa',
                    icon: 'pi pi-fw pi-users',
                    description: 'Gerencie processos administrativos e cadastrais de forma eficiente. Mantenha o controle de documentos, processos internos e informações de colaboradores.',
                    command: () => this.executar('Gestão Administrativa', '/gestao-administrativa')
                },
                {
                    label: 'Gestão de Comissões',
                    icon: 'pi pi-fw pi-sitemap',
                    description: 'Acompanhe reuniões, atas, convocações e atividades das comissões em uma visão gerencial consolidada.',
                    command: () => this.executar('Gestão de Comissões', '/gestao-comissoes')
                }
            ]
        },
        {
            label: 'Financeiro',
            items: [
                {
                    label: 'Arrecadação',
                    icon: 'pi pi-fw pi-dollar',
                    description: 'Gerencie e acompanhe o fluxo de arrecadação e receitas de forma simplificada.',
                    permission: PERMISSOES.arrecadacao.modulo,
                    command: () => this.executar('Arrecadação', '/arrecadacao')
                },
                {
                    label: 'Dívida Ativa',
                    icon: 'pi pi-fw pi-file-excel',
                    description: 'Consulte e gerencie registros de dívida ativa com relatórios detalhados.',
                    permission: PERMISSOES.dividaAtiva.modulo,
                    command: () => this.executar('Dívida Ativa', '/divida-ativa')
                }
            ]
        },
        {
            label: 'Registro',
            items: [
                {
                    label: 'Médicos',
                    icon: 'pi pi-fw pi-id-card',
                    description: 'Acesse o cadastro completo e o histórico profissional dos médicos registrados.',
                    permission: PERMISSOES.medicos.modulo,
                    command: () => this.executar('Medicos', '/medicos')
                },
                {
                    label: 'Prestadores',
                    icon: 'pi pi-fw pi-building',
                    description: 'Controle e gerencie o cadastro de prestadores de serviços de saúde.',
                    permission: PERMISSOES.prestadores.modulo,
                    command: () => this.executar('Prestadores', '/prestadores')
                },
                {
                    label: 'Especialidades',
                    icon: 'pi pi-fw pi-tags',
                    description: 'Gerencie as especialidades médicas e áreas de atuação cadastradas.',
                    ribbon: {
                        show: true,
                        text: 'Em breve',
                        color: 'var(--primary-color)'
                    }
                    // command: () => this.executar('Especialidades', '/especialidades')
                },
                {
                    label: 'Órgãos e Delegacias',
                    icon: 'pi pi-fw pi-map-marker',
                    description: 'Gerencie o cadastro de órgãos, delegacias e representações regionais.',
                    permission: PERMISSOES.orgaos.modulo,
                    command: () => this.executar('Órgãos e Delegacias', '/orgao')
                }
            ]
        },
        {
            label: 'Serviços',
            items: [
                {
                    label: 'Pessoa Física',
                    icon: 'pi pi-fw pi-user',
                    // badge: '31',
                    description: 'Solicite e acompanhe serviços destinados a pessoas físicas.',
                    permission: PERMISSOES.pessoaFisica.servicos.visualizar,
                    command: () => this.executar('Serviços', '/servicos/pessoa-fisica')
                },
                {
                    label: 'Pessoa Jurídica',
                    icon: 'pi pi-fw pi-briefcase',
                    // -- badge: '31',
                    description: 'Acesse ferramentas e serviços exclusivos para pessoas jurídicas.',
                    permission: PERMISSOES.pessoaJuridica.servicos.visualizar,
                    command: () => this.executar('Serviços', '/servicos/pessoa-juridica')
                }
            ]
        },
        {
            label: 'Comissões',
            items: [
                {
                    label: 'Comissões e Câmaras Técnicas',
                    icon: 'pi pi-fw pi-user',
                    description: 'Acompanhe reuniões, atas, membros, atividades.',
                    ribbon: {
                        show: false,
                        text: 'Em breve',
                        color: 'var(--primary-color)'
                    },
                    permission: PERMISSOES.comissoes.modulo,
                    command: () => this.executar('Comissões', '/comissoes/dashboard')
                }
            ]
        },
        // {
        //     label: 'Solicitações',
        //     items: [
        //         {
        //             label: 'Orientação Técnica',
        //             icon: 'pi pi-fw pi-question-circle',
        //             // badge: '31',
        //             description: 'Consulte orientações técnicas e esclareça dúvidas sobre normas e procedimentos.',
        //             ribbon: {
        //                 show: true,
        //                 text: 'Em breve',
        //                 color: 'var(--primary-color)'
        //             }
        //             // command: () => this.executar('Orientações Técnicas', '/orientacoes')
        //         }
        //     ]
        // },
        {
            label: 'Consulta',
            items: [
                {
                    label: 'Receita',
                    icon: 'pi pi-fw pi-search-plus',
                    description: 'Realize consultas rápidas sobre receitas e dados financeiros relacionados.',
                    permission: PERMISSOES.receita.consulta.visualizar,
                    command: () => this.toggleReceitaPesquisa()
                }
            ]
        },
        {
            label: 'Sistema',
            items: [
                {
                    label: 'Configurações',
                    icon: 'pi pi-fw pi-cog',
                    description: 'Acesse as configurações do sistema para personalizar e ajustar as funcionalidades.',
                    permission: [PERMISSOES.configuracoes.modulo, PERMISSOES.configuracoes.usuarios.permissoes.administrar],
                    command: () => this.executar('Configurações', 'configuracoes')
                }
            ]
        }
    ];

    @ViewChild('mosaicGrid') mosaicGrid!: ElementRef;

    /** Cards do bloco principal: vêm de WEB_MODULO (filtrados por UF no backend) + Receita/Configurações fixos. */
    protected readonly menuItemsWithRoutes = signal<CustomMenuItem[]>([]);
    protected readonly gestaoMenuItemsWithRoutes = this.buildGestaoMenuItemsWithRoutes();

    showReceitaPesquisa = false;
    private parallaxAnimationFrameId: number | null = null;
    private parallaxMouseMoveListener?: (event: MouseEvent) => void;

    ngOnInit(): void {
        this.menuService.setSistema('SICOM');
        this.menuService.setMenu(this.marcarEmBreve(this.cloneMenuItems(this.dashboardMenu)));
        this.initParallax();
        this.carregarModulos();
        this.carregarServicosPortal();
        this.carregarServicosCfm();
    }

    /**
     * Carrega os cards do bloco principal a partir de WEB_MODULO (já filtrado por UF no backend) e
     * anexa Receita e Configurações, que não estão na tabela. Em falha da API, mantém ao menos os
     * cards fixos para o dashboard não ficar vazio.
     */
    private carregarModulos(): void {
        this.moduloService
            .listar()
            .pipe(
                catchError(() => of<PortalModulo[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((modulos) => {
                const cards = (modulos ?? []).map((modulo) => this.moduloParaCard(modulo));
                this.menuItemsWithRoutes.set([...cards, ...this.cardsFixos()]);
            });
    }

    /**
     * Converte um módulo da tabela em card. Módulos inativos nem chegam aqui (o backend os filtra);
     * o gate é só pelas roles, via {@link estadoDoCard}, como nos cards fixos — sem a role o card
     * fica com a tarja RESTRITO e não navega.
     */
    private moduloParaCard(modulo: PortalModulo): CustomMenuItem {
        const item: CustomMenuItem = {
            label: modulo.nmModulo,
            description: modulo.dsModulo,
            icon: modulo.dsClassIcon ?? 'pi pi-fw pi-th-large',
            permission: this.permissaoPorUrl[modulo.url],
            command: () => this.executar(modulo.nmModulo, modulo.url)
        };
        return { ...item, estado: estadoDoCard(item, this.permissionService) };
    }

    /** Receita e Configurações não estão em WEB_MODULO; seguem fixos, ao fim da lista, com seu gate de permissão. */
    private cardsFixos(): CustomMenuItem[] {
        const fixos: CustomMenuItem[] = [
            {
                label: 'Receita',
                icon: 'pi pi-fw pi-search-plus',
                description: 'Realize consultas rápidas sobre receitas e dados financeiros relacionados.',
                permission: PERMISSOES.receita.consulta.visualizar,
                command: () => this.toggleReceitaPesquisa()
            },
            {
                label: 'Configurações',
                icon: 'pi pi-fw pi-cog',
                description: 'Acesse as configurações do sistema para personalizar e ajustar as funcionalidades.',
                permission: [PERMISSOES.configuracoes.modulo, PERMISSOES.configuracoes.usuarios.permissoes.administrar],
                command: () => this.executar('Configurações', 'configuracoes')
            }
        ];
        return fixos.map((item) => ({ ...item, estado: estadoDoCard(item, this.permissionService) }));
    }

    /** Bloco do regional: serviços não divulgados (divulga=false) da UF do usuário logado. */
    private carregarServicosPortal(): void {
        this.perfilService
            .buscarPerfil()
            .pipe(
                tap((res) => {
                    const perfil = Array.isArray(res) ? res[0] : res;
                    this.uf.set((perfil?.uf ?? '').trim().toUpperCase());
                }),
                switchMap(() => {
                    const uf = this.uf();
                    return uf ? this.servicoService.listarPorUf(uf, false) : of<PortalServico[]>([]);
                }),
                catchError(() => of<PortalServico[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((lista) => this.servicosPortal.set(lista ?? []));
    }

    /** Bloco do CFM: serviços divulgados (uf=BR, divulga=true), independente da UF do usuário. */
    private carregarServicosCfm(): void {
        this.servicoService
            .listarPorUf('BR', true)
            .pipe(
                catchError(() => of<PortalServico[]>([])),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((lista) => this.servicosCfm.set(lista ?? []));
    }

    ngOnDestroy(): void {
        if (this.parallaxAnimationFrameId !== null) {
            cancelAnimationFrame(this.parallaxAnimationFrameId);
            this.parallaxAnimationFrameId = null;
        }

        if (this.parallaxMouseMoveListener && typeof window !== 'undefined') {
            window.removeEventListener('mousemove', this.parallaxMouseMoveListener);
            this.parallaxMouseMoveListener = undefined;
        }
    }

    private initParallax() {
        if (typeof window === 'undefined') return;

        let targetX = 0,
            targetY = 0,
            currentX = 0,
            currentY = 0;

        this.parallaxMouseMoveListener = (e) => {
            const centerX = window.innerWidth / 2;
            const centerY = window.innerHeight / 2;
            targetX = (centerX - e.clientX) * 0.2;
            targetY = (centerY - e.clientY) * 0.2;
        };

        window.addEventListener('mousemove', this.parallaxMouseMoveListener);

        const update = () => {
            currentX += (targetX - currentX) * 0.05;
            currentY += (targetY - currentY) * 0.05;

            if (this.mosaicGrid) {
                const nodes = this.mosaicGrid.nativeElement.querySelectorAll('.mosaic-card');
                nodes.forEach((node: any, index: number) => {
                    const depthAttr = node.getAttribute('data-depth');
                    const depth = depthAttr ? parseFloat(depthAttr) : 0.02 + (index % 5) * 0.02;
                    const isHovered = node.matches(':hover');
                    const factor = isHovered ? 0 : 1;
                    const x = currentX * depth * factor;
                    const y = currentY * depth * factor;
                    node.style.transform = `translate3d(${x}px, ${y}px, 0)`;
                });
            }
            this.parallaxAnimationFrameId = requestAnimationFrame(update);
        };
        update();
    }

    private executar(sistema: string, rota: string) {
        this.menuService.setSistema('SICOM | ' + sistema);
        this.router.navigate([rota]);
    }

    protected readonly item = item;

    protected navigateToRoute(item: CustomMenuItem): void {
        if (item.estado !== 'normal') return;
        if (item.routerLink) {
            this.router.navigate(item.routerLink as unknown[]);
        } else if (item.command) {
            item.command({} as never);
        }
    }

    private buildGestaoMenuItemsWithRoutes(): CustomMenuItem[] {
        return this.dashboardMenu
            .filter((menuGroup) => menuGroup.label === 'Gestão')
            .flatMap((menuGroup) => menuGroup.items ?? [])
            .filter((item) => item.label !== 'Dashboard')
            .map((item) => ({ ...item, estado: estadoDoCard(item, this.permissionService) }));
    }

    private cloneMenuItems(items: CustomMenuItem[]): CustomMenuItem[] {
        return items.map((item) => ({
            ...item,
            ribbon: item.ribbon ? { ...item.ribbon } : undefined,
            items: item.items ? this.cloneMenuItems(item.items) : undefined
        }));
    }

    /** Só no MENU LATERAL: marca itens "Em breve" (ribbon.show) como desabilitados + selo. Os cards não recebem isso. */
    private marcarEmBreve(items: CustomMenuItem[]): CustomMenuItem[] {
        return items.map((item) => {
            const marcado: CustomMenuItem = { ...item, items: item.items ? this.marcarEmBreve(item.items) : undefined };
            if (item.ribbon?.show) {
                marcado.disabled = true;
                marcado.badge = 'Em breve';
            }
            return marcado;
        });
    }

    protected navigateToExternalUrl(url: string): void {
        window.open(url, '_blank');
    }

    toggleReceitaPesquisa() {
        if (!this.permissionService.can(PERMISSOES.receita.consulta.visualizar)) return;
        this.showReceitaPesquisa = !this.showReceitaPesquisa;
    }

    protected readonly EInconPos = EInconPos;
}
